<template>
    <div class="signature-history-list">
        <div class="list-header">
            <h4>
                <i class="fa fa-history"></i>
                Signature History
            </h4>
            <span v-if="signatures.length > 0" class="count-badge">
                {{ signatures.length }}
            </span>
        </div>

        <div v-if="signatures.length === 0" class="empty-state">
            <i class="fa fa-inbox"></i>
            <p>No signatures yet</p>
        </div>

        <div v-else class="history-content">
            <div
                v-for="signature in sortedSignatures"
                :key="signature.id"
                class="history-item"
            >
                <div class="signature-icon">
                    <i class="fa fa-check-circle"></i>
                </div>

                <div class="signature-details">
                    <div class="signature-header">
                        <span
                            class="signee-badge"
                            :class="`badge-${signature.signee_type}`"
                        >
                            {{ formatSigneeType(signature.signee_type) }}
                        </span>
                        <span v-if="signature.document_field_id" class="field-label">
                            <i class="fa fa-wpforms"></i>
                            Field Signature
                        </span>
                        <span v-else class="field-label">
                            <i class="fa fa-file-alt"></i>
                            Document Signature
                        </span>
                    </div>

                    <div class="signer-info">
                        <strong>{{ formatSignerName(signature) }}</strong>
                        <span class="email">{{ signature.user?.email }}</span>
                    </div>

                    <div class="timestamp">
                        <i class="fa fa-clock"></i>
                        {{ formatTimestamp(signature.signed_at) }}
                    </div>

                    <!-- Optional: Signature Thumbnail -->
                    <div v-if="showThumbnails && signature.signature_data" class="signature-thumbnail">
                        <img :src="signature.signature_data" alt="Signature" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SignatureHistoryList',
    props: {
        signatures: {
            type: Array,
            default: () => []
        },
        showThumbnails: {
            type: Boolean,
            default: false
        },
        studentName: {
            type: String,
            default: ''
        }
    },
    computed: {
        sortedSignatures() {
            // Sort by signed_at timestamp, most recent first
            return [...this.signatures].sort((a, b) => {
                const dateA = new Date(a.signed_at);
                const dateB = new Date(b.signed_at);
                return dateB - dateA;
            });
        }
    },
    methods: {
        formatSignerName(signature) {
            if (signature.signee_type === 'guardian' && this.studentName) {
                return `${this.studentName} (Guardian)`;
            }
            return signature.user?.name || 'Unknown';
        },

        formatSigneeType(type) {
            const labels = {
                student: 'Student',
                guardian: 'Guardian',
                instructor: 'Instructor',
                staff: 'Staff',
                admin: 'Admin'
            };
            return labels[type] || type;
        },

        formatTimestamp(timestamp) {
            if (!timestamp) return 'Unknown date';

            const date = new Date(timestamp);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) {
                return 'Just now';
            } else if (diffMins < 60) {
                return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
            } else if (diffHours < 24) {
                return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
            } else if (diffDays < 7) {
                return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
            } else {
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
        }
    }
};
</script>

<style scoped>
.signature-history-list {
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
}

.list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background-color: #f7fafc;
    border-bottom: 1px solid #e2e8f0;
}

.list-header h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 8px;
}

.list-header h4 i {
    color: #4a5568;
}

.count-badge {
    background-color: #cbd5e0;
    color: #2d3748;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 12px;
    min-width: 24px;
    text-align: center;
}

.empty-state {
    padding: 40px 20px;
    text-align: center;
    color: #a0aec0;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
    color: #718096;
}

.history-content {
    padding: 0;
    max-height: 500px;
    overflow-y: auto;
}

.history-item {
    display: flex;
    gap: 14px;
    padding: 16px 20px;
    border-bottom: 1px solid #f7fafc;
    transition: all 0.2s;
}

.history-item:hover {
    background-color: #f7fafc;
}

.history-item:last-child {
    border-bottom: none;
}

.signature-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #d1fae5;
    border-radius: 50%;
    color: #065f46;
}

.signature-icon i {
    font-size: 18px;
}

.signature-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.signature-header {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.signee-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.badge-student {
    background-color: #3b82f6;
}

.badge-guardian {
    background-color: #10b981;
}

.badge-instructor {
    background-color: #f59e0b;
}

.badge-staff {
    background-color: #8b5cf6;
}

.badge-admin {
    background-color: #ef4444;
}

.field-label {
    font-size: 11px;
    color: #718096;
    display: flex;
    align-items: center;
    gap: 4px;
}

.field-label i {
    font-size: 10px;
}

.signer-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.signer-info strong {
    font-size: 14px;
    color: #2d3748;
}

.signer-info .email {
    font-size: 12px;
    color: #718096;
}

.timestamp {
    font-size: 12px;
    color: #a0aec0;
    display: flex;
    align-items: center;
    gap: 4px;
}

.timestamp i {
    font-size: 10px;
}

.signature-thumbnail {
    margin-top: 8px;
    padding: 8px;
    background-color: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    max-width: 200px;
}

.signature-thumbnail img {
    width: 100%;
    height: auto;
    display: block;
}

/* Mobile Optimization */
@media (max-width: 768px) {
    .list-header {
        padding: 12px 16px;
    }

    .list-header h4 {
        font-size: 15px;
    }

    .history-item {
        padding: 12px 16px;
    }

    .signature-icon {
        width: 36px;
        height: 36px;
    }

    .signature-icon i {
        font-size: 16px;
    }

    .signer-info strong {
        font-size: 13px;
    }

    .signer-info .email {
        font-size: 11px;
    }
}
</style>
